import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InputNumber from "../../../../components/InputNumber/InputNumber";
import InputShortText from "../../../../components/InputShortText/InputShortText";
import Notice from "../../../../components/Notice/Notice";
import SecondaryButton from "../../../../components/SecondaryButton/SecondaryButton";
import SmallCheckBox from "../../../../components/SmallCheckBox/SmallCheckBox";
import Subtitle from "../../../../components/Subtitle/Subtitle";
import TextArea from "../../../../components/TextArea/TextArea";
import TinyText from "../../../../components/TinyText/TinyText";
import CONFIG from "../../../../config/config";
import { isValidText } from "../../../../shared/validation";
import { UpdateCallback } from "../../sections/NotificationsSegment";

interface VisitReminderProps {
  update: UpdateCallback;
  defaultConfig?: VisitReminderConfig;
}

interface ReminderDetails {
  // phoneId?: string; // Commented out - not currently used in backend SMS sending
  emailTitle?: string;
  text?: string;
  daysInAdvance?: number;
}

export interface VisitReminderConfig {
    enabled: boolean;
    type: 'sms' | 'email';
    details?: ReminderDetails;
}

export function VisitReminder({ update, defaultConfig }: VisitReminderProps) {
  const [enabled, setEnabled] = useState(defaultConfig?.enabled ?? false);
  const [type, setType] = useState(defaultConfig?.type ?? 'sms');
  const [details, setDetails] = useState<ReminderDetails>(defaultConfig?.details || {});
  const { t } = useTranslation();
  // const MAX_SENDER_ID_LENGTH = 11;

  useEffect(() => {
    if (defaultConfig) {
      setEnabled(defaultConfig.enabled || false);
      setType(defaultConfig.type || 'sms');
      setDetails(defaultConfig.details || {});
    }
  }, [defaultConfig]);

  useEffect(() => {
    const config: VisitReminderConfig = {
      enabled,
      type,
      details
    };
    update('smsReminder', config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, type, details]);

  const injectDefaultTemplate = () => {
    const baseDetails = {
      ...details,
      text: String(t("notifications.visit_reminder.default_message")),
    };

    if (type === "email" && !details.emailTitle) {
      setDetails({
        ...baseDetails,
        emailTitle: String(t("notifications.visit_reminder.default_email_title")),
      });
    } else {
      setDetails(baseDetails);
    }
  };

  return (
    <>
      <Subtitle text={t("notifications.visit_reminder")} />
      <TinyText text={t("notifications.visit_reminder.description")} />
      <SmallCheckBox
        label={t("global.enabled")!}
        onChange={() => setEnabled(prev => !prev)}
        defaultValue={enabled}
      />
      <br/>
      <InputNumber
          label={t("notifications.visit_reminder.days_in_advance")!}
          validationFn={(value) => !!value && value >= 1}
          onChange={e => setDetails({ ...details, daysInAdvance: Number(e.currentTarget.value) })}
          defaultValue={details.daysInAdvance || 2}
          errorMessage={"errors.minimum_zero"}
      />
      <div className="mt-3">
        <p className="text-sm font-extrabold">
          {t("notifications.visit_reminder.channel")}:
        </p>
        <div className="mt-2 inline-flex rounded-md border border-stone-200 bg-white overflow-hidden">
          {CONFIG.NOTIFICATION_CHANNELS.map((channel) => (
            <button
              key={channel}
              type="button"
              onClick={() => setType(channel as "sms" | "email")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                type === channel
                  ? "bg-stone-900 text-white"
                  : "bg-white text-stone-700 hover:bg-stone-100"
              } ${channel === "sms" ? "border-r border-stone-200" : ""}`}
            >
              {channel.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      {/* Not used for SMS for now */}
      {/* <InputShortText
        label={type === 'sms' ? t("notifications.visit_reminder.phone_id")! : t("notifications.visit_reminder.email_title")!}
        validationFn={(value) => isValidText(value, 1, type === 'sms' ? MAX_SENDER_ID_LENGTH : 50)}
        onChange={(e: any) => setDetails({ ...details, [type === 'sms' ? 'phoneId' : 'emailTitle']: e.target.value })}
        errorMessage={"errors.too_long"}
        defaultValue={type === 'sms' ? details.phoneId : details.emailTitle}
      /> */}
      {type === 'email' && (
        <div className="mt-3">
          <InputShortText
            label={t("notifications.visit_reminder.email_title")!}
            validationFn={(value) => value === "" || isValidText(value, 1, 50)}
            onChange={(e: any) => setDetails({ ...details, emailTitle: e.target.value })}
            errorMessage={"errors.too_long"}
            defaultValue={details.emailTitle}
          />
        </div>
      )}
      <Notice 
        pillText={t("global.notice")} 
        tinyText={t("notifications.visit_reminder.message.keywords")}
      />
      <TextArea
        label={t("notifications.visit_reminder.message")!}
        onChange={e => setDetails({ ...details, text: e.currentTarget.value })}
        defaultValue={details.text}
        errorMessage={"errors.too_long"}
        maxLength={type === 'sms' ? 300 : 700}
      />
      <div className="mt-3">
        <SecondaryButton
          text={String(t("notifications.visit_reminder.use_default_template"))}
          fn={injectDefaultTemplate}
        />
      </div>
    </>
  );
}
